import cn from 'classnames';
import FocusTrap from 'focus-trap-react';
import Link from 'next/link';
import * as React from 'react';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { usePageContext } from '@/features/page/context';
import { disableScroll, enableScroll } from '@/helpers/common';
import usePublicTranslation from '@/hooks/usePublicTranslation';

import styles from './styles.module.scss';

export interface ButtonPopupProps {
  buttonText?: string;
  children?: ReactNode | ReactNode[];
}

const ButtonPopup: React.FC<ButtonPopupProps> = ({ buttonText, children, ...rest }) => {
  const t = usePublicTranslation();

  const [popupIsActive, setPopupIsActive] = useState<boolean>(false);

  const { generalSettings } = usePageContext();

  const handleOpen = useCallback(() => {
    setPopupIsActive(true);
    disableScroll();
  }, []);
  const handleClose = useCallback(() => {
    setPopupIsActive(false);
    enableScroll();
  }, []);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && popupIsActive) {
        handleClose();
      }
    },
    [popupIsActive, handleClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape, false);
    return () => {
      document.removeEventListener('keydown', handleEscape, false);
    };
  }, [handleEscape]);

  useEffect(() => {
    return () => {
      enableScroll();
    };
  }, []);

  return (
    <div {...rest}>
      <button
        className={cn(styles.popupButton, 'wp-block-erom-button-popup-trigger')}
        type="button"
        onClick={handleOpen}
        dangerouslySetInnerHTML={{ __html: buttonText || '' }}
      />
      {popupIsActive && (
        <FocusTrap>
          <div className={cn(styles.popupModal, 'modal')} aria-modal={true} role="dialog">
            <div
              className={cn(styles.popupHeader, 'header', {
                [styles.popupHeaderWithLogo]: !!generalSettings,
                ['popup-header-with-logo']: !!generalSettings
              })}
            >
              {generalSettings && (
                <Link href="/">
                  {
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={generalSettings?.logo}
                      alt={generalSettings?.site_name}
                      className={cn(styles.popupLogo, 'logo')}
                    />
                  }
                </Link>
              )}
              <button className={cn(styles.popupClose, 'close')} onClick={handleClose}>
                <i />
                <span>{t('button_popup.close')}</span>
              </button>
            </div>
            <div className={cn(styles.popupContent, 'wp-block-erom-button-popup-content')}>{children}</div>
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default ButtonPopup;
